// SQLite de verdade compilado para WASM (sql.js), atrás do mesmo contrato
// clássico WebSQL que src/db/client.ts já usa via expo-sqlite no nativo —
// nenhuma outra camada do app (repositórios, outbox, sync, telas) precisa
// saber que está rodando no navegador. Ver
// docs/crochet/specs/2026-07-21-S08-web-sqlite-wasm-implementacao.md para
// o porquê de sql.js (e não wa-sqlite/OPFS: offline não é requisito aqui,
// então a complexidade de um VFS durável em Worker não se paga).
//
// `openDatabase()` PRECISA devolver o objeto WebSQLDatabase-shaped na
// hora, de forma síncrona — client.ts (`getRawDatabase`) espera isso,
// igual ao `expo-sqlite` nativo. Como carregar o `.wasm` é assíncrono
// (initSqlJs), a espera é escondida dentro de `transaction()`: cada
// chamada aguarda o banco ficar pronto antes de rodar o callback, mas o
// objeto em si já existe desde o primeiro instante.
import initSqlJs, { type Database as SqlJsDatabase } from 'sql.js';
import type { WebSQLDatabase, SQLTransaction, SQLResultSet } from 'expo-sqlite';
import { loadSnapshot, saveSnapshot } from './webSnapshotStore';

export function openDatabase(_name: string): WebSQLDatabase {
  // Escopo por chamada de `openDatabase()`, não a nível de módulo: o
  // critério de aceite inclui "um F5 recarrega o que foi salvo", e um
  // recarregamento de página é justamente uma nova chamada a
  // `openDatabase()` — precisa ler o snapshot de novo do zero. Múltiplas
  // `.transaction()` no MESMO objeto devolvido (o uso real em
  // src/db/client.ts, que chama `openDatabase()` uma única vez e reusa o
  // resultado pelo resto da sessão) continuam compartilhando este mesmo
  // `readyPromise` — só carrega o banco uma vez por sessão, não uma vez por
  // transação.
  let readyPromise: Promise<SqlJsDatabase> | null = null;

  // Também por chamada de `openDatabase()`, ao lado de `readyPromise`, pelo
  // mesmo motivo: drizzle-orm/sqlite-proxy (usado por todo write real do
  // app — insertRecord/updateRecord/softDeleteRecord em src/db/writes.ts)
  // não manda um `begin`...`commit` dentro de UMA chamada a
  // `.transaction()` — manda "begin", depois cada statement, depois
  // "commit"/"rollback", cada um como sua PRÓPRIA chamada top-level
  // separada a `.transaction()` (client.ts's execute() embrulha cada
  // texto SQL individual num `.transaction()` próprio). Este flag guarda,
  // entre essas chamadas separadas, se já vimos um "begin" sem o
  // "commit"/"rollback" correspondente ainda — para saber que ainda
  // estamos NO MEIO de uma transação real do sql.js e não podemos
  // exportar o banco agora (ver comentário dentro de `.transaction()`).
  // PONYTAIL: single shared flag assumes sequential (not concurrent) transactions;
  // all write call sites are sequential await today, accepted ceiling.
  let inTransaction = false;

  function getReadyDb(): Promise<SqlJsDatabase> {
    if (!readyPromise) {
      readyPromise = (async () => {
        // No navegador, o .wasm é servido como asset estático na raiz do site
        // (web/sql-wasm.wasm — ver comentário no topo do arquivo), daí o
        // `locateFile` apontando para `/${file}`. Em Node (Jest), não existe
        // servidor nenhum: omitir `locateFile` deixa o próprio sql.js usar seu
        // fallback padrão, que já resolve o arquivo corretamente ao lado do
        // seu próprio módulo em node_modules/sql.js/dist — evita reintroduzir
        // aqui uma segunda lógica de resolução de caminho (fs/path) que só
        // duplicaria o que o sql.js já faz.
        //
        // Detecção de ambiente via `typeof document` (não `typeof window`): o
        // preset de teste `react-native` do Jest define `global.window` (para
        // compatibilidade com código RN que assume `window` existir), mas não
        // `document` — só um browser de verdade tem os dois.
        const SQL = await initSqlJs(typeof document === 'undefined' ? undefined : { locateFile: (file) => `/${file}` });
        const snapshot = await loadSnapshot();
        return snapshot ? new SQL.Database(snapshot) : new SQL.Database();
      })();
    }
    return readyPromise;
  }

  return {
    transaction(
      callback: (tx: SQLTransaction) => void,
      errorCallback?: (error: any) => void,
      successCallback?: () => void
    ) {
      getReadyDb()
        .then(async (db) => {
          // Convenção WebSQL (mesma do fake usado em client.test.ts): o
          // errorCallback de cada statement devolve `false` para dizer "eu
          // tratei, não aborte a transação" — qualquer outra coisa (incluindo
          // não ter passado callback nenhum, o caso de runMigrations() e
          // ensureOutboxQuarantineColumns() em client.ts) aborta a
          // transação inteira: successCallback nunca dispara, e o snapshot
          // não é salvo, para não persistir um estado que teve erro não
          // tratado.
          let transactionError: unknown = null;
          // Uma transação só de leitura (qualquer combinação de SELECTs) não
          // precisa exportar+persistir nada — só estatísticas mutantes (tudo
          // que não é SELECT) podem ter mudado o conteúdo do banco. Marcado
          // já na entrada de executeSql (antes do try/prepare), não só em
          // caso de sucesso: mesmo uma tentativa de escrita que falhe conta
          // como "essa transação não era só leitura" — mas isso é moot aqui,
          // porque um erro não tratado já aborta pra errorCallback antes de
          // chegar no export (ver abaixo), então o flag só importa mesmo no
          // caminho de sucesso.
          let hasMutation = false;
          // drizzle-orm/sqlite-proxy sempre manda "begin"/"commit"/"rollback"
          // como statement dedicado, nunca misturado com outro SQL na mesma
          // chamada de executeSql — daí bastar comparar o texto inteiro
          // (normalizado), sem precisar de um parser de verdade.
          let sawBegin = false;
          let sawCommitOrRollback = false;
          const tx: SQLTransaction = {
            executeSql(sqlStatement, args, success, error) {
              const normalized = sqlStatement.trim().toLowerCase();
              if (normalized.startsWith('begin')) {
                sawBegin = true;
              } else if (normalized === 'commit' || normalized === 'rollback') {
                sawCommitOrRollback = true;
              }
              const isSelect = /^\s*select/i.test(sqlStatement);
              if (!isSelect) {
                hasMutation = true;
              }
              try {
                const stmt = db.prepare(sqlStatement);
                try {
                  if (args && args.length > 0) {
                    stmt.bind(args as (string | number)[]);
                  }
                  const rowsArray: Record<string, unknown>[] = [];
                  if (isSelect) {
                    while (stmt.step()) {
                      rowsArray.push(stmt.getAsObject());
                    }
                  } else {
                    stmt.step();
                  }
                  const resultSet = {
                    rows: {
                      _array: rowsArray,
                      length: rowsArray.length,
                      item: (i: number) => rowsArray[i],
                    },
                    rowsAffected: 0,
                    insertId: undefined,
                  } as unknown as SQLResultSet;
                  success?.(tx, resultSet);
                } finally {
                  stmt.free();
                }
              } catch (err) {
                const handled = error?.(tx, err as any) === false;
                if (!handled && transactionError === null) {
                  transactionError = err;
                }
              }
            },
          } as SQLTransaction;

          callback(tx);

          if (transactionError !== null) {
            errorCallback?.(transactionError);
            return;
          }

          // "begin" abre uma transação real do sql.js que só fecha lá na
          // chamada separada que manda "commit"/"rollback" (ver comentário
          // de `inTransaction` acima) — atualiza o estado ANTES de decidir
          // se exporta.
          if (sawBegin) {
            inTransaction = true;
          }
          if (sawCommitOrRollback) {
            inTransaction = false;
          }

          // Snapshot depois de cada transação — bloqueia o successCallback
          // de propósito (não fire-and-forget): o critério de aceite é "um
          // F5 no meio de uma sessão não perde dados", e o custo de esperar
          // um export()+IndexedDB.put() (dezenas de ms, banco pequeno) é
          // aceitável contra o risco de perder a última escrita. Mas só
          // quando a transação teve alguma escrita de verdade (hasMutation)
          // — sem isso, todo SELECT (listagens, boot, pulls de sync) pagaria
          // um export()+put() do banco inteiro à toa (Finding 1 da revisão
          // final). E só quando NÃO estamos no meio de uma transação real
          // do sql.js que ainda vai continuar em outra chamada separada
          // (`inTransaction`) — dar export()+saveSnapshot() entre um
          // "begin" e seu "commit"/"rollback" corrompe o estado interno do
          // sql.js, fazendo o commit/rollback seguinte falhar com "cannot
          // commit/rollback - no transaction is active" (o bug real do
          // drizzle-orm/sqlite-proxy: begin/statements/commit chegam como
          // chamadas .transaction() top-level SEPARADAS, não uma só).
          if (hasMutation && !inTransaction) {
            const buffer = db.export();
            await saveSnapshot(buffer);
          }
          successCallback?.();
        })
        .catch((err) => errorCallback?.(err));
    },
  } as unknown as WebSQLDatabase;
}
